import React from 'react';
import { connect } from 'react-redux';
import googleCarousel1 from 'assets/images/backoffice/online-store/google-carousel-1-eng.png';
import googleCarousel2 from 'assets/images/backoffice/online-store/google-carousel-2-eng.png';
import googleCarousel3 from 'assets/images/backoffice/online-store/google-carousel-3-eng.png';
import googleCarousel4 from 'assets/images/backoffice/online-store/google-carousel-4-eng.png';
import googleLogoSmall from 'assets/images/backoffice/online-store/google-logo-small.png';
import TooltipHint from 'components/TooltipHint/TooltipHint';
import LandingPage from 'pages/OnlineStore/LandingPage/LandingPage';
import OutletModal from 'pages/OnlineStore/OutletModal/OutletModal';
import DisconnectShopModal from 'pages/OnlineStore/DisconnectShopModal/DisconnectShopModal';
import PageHeader from 'pages/OnlineStore/LandingPage/PageHeader';
import ProductSyncingModal from 'pages/OnlineStore/ProductSyncingModal/ProductSyncingModal';
import {
  startSelectOutletFlow,
  showDisconnectShopModal,
  startSyncingProductValidationFlow,
  pageRequest,
  openConnectGoogleEmailModal,
} from 'actions/onlineStore';
import {
  CONNECT_GOOGLE_SOURCE,
  ONLINE_STORE_CHANNEL,
  SHOULD_MOCK,
} from 'utils/onlineStoreUtils';
import isExperienceComplete from 'selectors/isExperienceComplete';
import { completeExperience } from 'reducers/ftue/actions';
import { FTUE_ONLINE_STORE_GOOGLE_RED_DOT } from 'reducers/ftue/ftueKeys';
import * as googleClevertap from 'clevertap/onlineStore/google';
import LearnMore from 'pages/OnlineStore/ConnectedOutletList/LearnMore/LearnMore';
import OutletColumn from 'pages/OnlineStore/ConnectedOutletList/OutletColumn/OutletColumn';
import ProductCountColumn from 'pages/OnlineStore/ConnectedOutletList/ProductCountColumn/ProductCountColumn';
import NotConnected from 'pages/OnlineStore/ConnectedOutletList/NotConnected/NotConnected';
import ShopActionColumn from 'pages/OnlineStore/ConnectedOutletList/ShopActionColumn/ShopActionColumn';
import GoStoreNotActivatedModal from 'pages/OnlineStore/GoStoreNotActivatedModal/GoStoreNotActivatedModal';
import GoStoreNotNationWideShippingModal from 'pages/OnlineStore/GoStoreNotNationWideShippingModal/GoStoreNotNationWideShippingModal';
import ConnectEmailModal from './ConnectEmailModal/ConnectEmailModal';
import styles from './Google.css';

const landingPageProps = {
  icons: [googleLogoSmall],
  pageTitle: 'GOOGLE SHOPPING',
  headingText: 'Display Your Products on Google Now!',
  headingDescription: (
    <span>
      Get your products discovered on <b>Google Shopping and Google Image</b>{' '}
      search by easily connecting Moka to Google
    </span>
  ),
  carouselImages: [
    googleCarousel1,
    googleCarousel2,
    googleCarousel3,
    googleCarousel4,
  ],
  ctaText: 'Connect to Google',
  howToUrl: 'https://mokaposhelp.zendesk.com/hc/id',
  howToDescription: 'Benefit of selling on Google:',
  howToItems: [
    'Your products will be discovered by users all over Indonesia.',
    'We help to upload & update your Moka items to Google anytime.',
    'Manage your inventory in one place: Moka!',
  ],
};

const gmailTooltip = (
  <span className={styles['tooltip-text']}>
    Register your Google email to see your product performance in{' '}
    <a
      href="https://www.google.com/retail/solutions/merchant-center/"
      target="_blank"
      className={styles['tooltip-link']}
    >
      Google Merchant Center
    </a>
  </span>
);

class Google extends React.Component {
  componentDidMount() {
    this.props.dispatch(pageRequest(ONLINE_STORE_CHANNEL.GOOGLE));

    if (this.props.showRedDot) {
      this.props.dispatch(completeExperience(FTUE_ONLINE_STORE_GOOGLE_RED_DOT));
    }
  }

  render() {
    return (
      <div className={styles.container}>
        {this._renderContent()}

        <OutletModal />
        <ProductSyncingModal />
        <DisconnectShopModal />
        <GoStoreNotActivatedModal />
        <GoStoreNotNationWideShippingModal />
        <ConnectEmailModal initialValues={this._getEmailModalInitialValues()} />
      </div>
    );
  }

  _getEmailModalInitialValues = () => {
    const { email } = this.props;

    if (!email || email.indexOf('@gmail.') === -1) {
      return null;
    }

    return { email };
  };

  _renderContent = () => {
    const { shops, pageRequestLoading } = this.props;

    if (pageRequestLoading && shops.length === 0) {
      return null;
    }

    if (shops.length === 0) {
      return (
        <LandingPage
          {...landingPageProps}
          onClick={this._handleLandingPageConnectGoogle}
        />
      );
    }

    const canConnectAnotherOutlet = shops.length < 1;

    return (
      <div className={styles['connected-outlet-list-container']}>
        <PageHeader
          icons={landingPageProps.icons}
          title={landingPageProps.pageTitle}
        />

        <div className={styles['sub-heading']}>
          <h2>Display Your Products on Google Now!</h2>
          <div>
            <p>
              Get your products discovered on{' '}
              <b>Google Shopping and Google Image search</b> by easily
              connecting Moka to Google.
            </p>
            {canConnectAnotherOutlet && (
              <button
                className="btn btn-primary"
                onClick={this._handleOutletListConnectGoogle}
              >
                Connect Another Outlet
              </button>
            )}
          </div>

          <hr />
        </div>

        <div className={styles['connected-outlet-list']}>
          <h3>Connected Outlet</h3>
          <table className={styles['outlet-table']}>
            <tr>
              <th>Outlet</th>
              <th>Google Shopping ID</th>
              <th>
                Google Email
                <TooltipHint color="#8F8F8F" tooltip={gmailTooltip} />
              </th>
              <th>Product Uploaded</th>
              <th />
            </tr>

            {this._renderShops()}
          </table>
        </div>

        <LearnMore
          url="https://mokaposhelp.zendesk.com/hc/id"
          description="Need more info on how to connect to Google Shopping?"
        />
      </div>
    );
  };

  _renderShops = () => {
    const { shops, outlets } = this.props;
    const notConnnectedEl = <NotConnected />;

    return shops
      .filter(shop => SHOULD_MOCK || outlets.some(o => o.id === shop.outlet_id))
      .map(shop => {
        const outlet = SHOULD_MOCK
          ? outlets[0]
          : outlets.filter(o => o.id === shop.outlet_id)[0];

        return (
          <tr key={shop.id}>
            <td>
              <OutletColumn outlet={outlet} shop={shop} />
            </td>
            <td>
              {shop.status === 'ACTIVE'
                ? this._renderGoogleShoppingId(shop, outlet)
                : notConnnectedEl}
            </td>
            <td>
              {shop.status === 'ACTIVE'
                ? this._renderGoogleEmail(shop)
                : notConnnectedEl}
            </td>
            <td>
              <ProductCountColumn
                shop={shop}
                onProductSync={this._handleStartSyncProduct}
              />
            </td>
            <td>
              <ShopActionColumn
                shop={shop}
                outlet={outlet}
                onDisconnect={this._handleDisconnect}
                onProductSync={this._handleStartSyncProduct}
              />
            </td>
          </tr>
        );
      });
  };

  _renderGoogleShoppingId = (shop, outlet) => {
    return (
      <div
        className={styles['google-shopping-id']}
        title={`${shop.additional.id} (${outlet.name})`}
      >
        <div>{shop.additional.id}</div>
        <div>({outlet.name})</div>
      </div>
    );
  };

  _renderGoogleEmail = shop => {
    if (shop.additional.email_address) {
      return (
        <span
          className={styles['text-grey']}
          title={shop.additional.email_address}
        >
          {shop.additional.email_address}
        </span>
      );
    }

    return (
      <a
        className={styles['text-underline']}
        onClick={() => this._handleOpenConnectEmailModal(shop)}
      >
        Connect
      </a>
    );
  };

  _handleOpenConnectEmailModal = shop => {
    this.props.dispatch(openConnectGoogleEmailModal(shop));
  };

  _handleLandingPageConnectGoogle = () => {
    googleClevertap.googleConnectAccountClick();
    this.props.dispatch(
      startSelectOutletFlow(
        ONLINE_STORE_CHANNEL.GOOGLE,
        CONNECT_GOOGLE_SOURCE.LANDING_PAGE
      )
    );
  };

  _handleOutletListConnectGoogle = () => {
    googleClevertap.googleConnectAnotherOutletClick();
    this.props.dispatch(
      startSelectOutletFlow(
        ONLINE_STORE_CHANNEL.GOOGLE,
        CONNECT_GOOGLE_SOURCE.OUTLET_LIST_PAGE
      )
    );
  };

  _handleDisconnect = (shop, outlet) => {
    googleClevertap.googleDisconnectClick();
    this.props.dispatch(showDisconnectShopModal(shop, outlet));
  };

  _handleStartSyncProduct = shop => {
    googleClevertap.googleUploadProductsClick();
    this.props.dispatch(startSyncingProductValidationFlow(shop.id, true));
  };
}

function mapStateToProps(state) {
  const {
    outlets,
    business: { email },
  } = state.moka.user;
  const {
    shop: { shops, pageRequestLoading },
  } = state.onlineStore;

  return {
    email,
    outlets,
    shops,
    pageRequestLoading,
    showRedDot: isExperienceComplete(state, FTUE_ONLINE_STORE_GOOGLE_RED_DOT),
  };
}

export default connect(mapStateToProps)(Google);
